/**
 * Upload a script and watch the agent read it.
 *
 * Two modes on one page: the checked-in snapshot (instant, always works, safe to open a
 * demo on) and a live run (proves it is a real agent and not a screenshot). This is the
 * live half. It calls the backend directly and hands the finished run up to the page,
 * which renders it with exactly the same code that renders the snapshot.
 *
 * Endpoints are RELATIVE (`/api/v1/...`) rather than built from VITE_API_BASE_URL, which
 * the rest of the app uses. Vite proxies /api to localhost:8000, so a relative path works
 * both at localhost:5137 and over the LAN at 192.168.x.x:5137 — and demoing from a phone
 * or a second laptop is exactly when an absolute http://localhost:8000 breaks.
 *
 * Polling, not a websocket or SSE: a run is minutes long and the payload is a progress
 * integer. There is an SSE router in this app, but wiring into it would mean the demo
 * depends on that path being healthy.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, PlayCircle, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type { PlacementRun, RunJob } from "@/types/placement-run";

const START_URL = "/api/v1/placements/demo/run";
const POLL_MS = 2000;

interface Props {
  onComplete: (run: PlacementRun) => void;
}

export default function RunScriptPanel({ onComplete }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [job, setJob] = useState<RunJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef<number | null>(null);
  const logEnd = useRef<HTMLDivElement | null>(null);

  const stopPolling = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // Stop polling if the page goes away mid-run; the run itself keeps going server-side.
  useEffect(() => stopPolling, [stopPolling]);

  useEffect(() => {
    logEnd.current?.scrollIntoView({ block: "nearest" });
  }, [job?.log.length]);

  const poll = useCallback(
    async (jobId: string) => {
      try {
        const res = await fetch(`${START_URL}/${jobId}`);
        if (!res.ok) throw new Error(`poll failed: ${res.status}`);
        const next: RunJob = await res.json();
        setJob(next);
        if (next.status === "running") {
          timer.current = window.setTimeout(() => poll(jobId), POLL_MS);
        } else if (next.status === "done" && next.result) {
          onComplete(next.result);
        } else if (next.status === "failed") {
          setError(next.error ?? "The run failed");
        }
      } catch (e) {
        // A dropped poll is not a dead run — the backend may just have restarted. Say
        // which one it was rather than blaming the agent.
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [onComplete],
  );

  const start = async () => {
    if (!file) return;
    setError(null);
    setJob(null);
    setStarting(true);
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("show", file.name.replace(/\.[^.]+$/, "").toUpperCase().slice(0, 24));
      body.append("episode", "101");
      const res = await fetch(START_URL, { method: "POST", body });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        // FastAPI puts the reason in `detail`, and these reasons are the useful part:
        // "lost its screenplay layout", "a run is already going", the size limit.
        throw new Error(payload?.detail ?? `${res.status} ${res.statusText}`);
      }
      setJob(payload as RunJob);
      timer.current = window.setTimeout(() => poll(payload.job_id), POLL_MS);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  };

  const running = job?.status === "running";

  return (
    <Card className="border-dashed">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Upload a script</CardTitle>
        <CardDescription>
          Reads the script scene by scene and prices every product it finds. A 13-scene
          episode takes about 3 minutes and costs roughly $0.80.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted">
            <Upload className="h-4 w-4" />
            {file ? file.name : "Choose a .pdf or .txt"}
            <input
              type="file"
              accept=".pdf,.txt,.fountain"
              className="hidden"
              disabled={running || starting}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setError(null);
              }}
            />
          </label>
          <Button onClick={start} disabled={!file || running || starting}>
            {running || starting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PlayCircle className="mr-2 h-4 w-4" />
            )}
            {running ? "Reading…" : starting ? "Starting…" : "Run agent"}
          </Button>
          {job && (
            <span className="text-xs text-muted-foreground">
              {job.script} · {job.elapsed_seconds}s elapsed
            </span>
          )}
        </div>

        {/* Upload a PDF, not text pasted out of one: a screenplay marks spoken lines by
            indenting them, and that indentation is what decides 6 seconds vs 15. The
            backend rejects flattened text rather than pricing it wrong. */}
        <p className="text-xs text-muted-foreground">
          Upload the original PDF. Text copied out of one loses its indentation, and the
          indentation is what separates a line a character <em>says</em> from one the
          camera <em>sees</em> — which is the difference between 6 seconds and 15.
        </p>
        {/* Worth knowing before someone asks "did that really just run?" mid-demo: the
            agent keeps a local replay cache, so a script already run on this machine comes
            back in seconds with the model calls skipped entirely. */}
        <p className="text-xs text-muted-foreground">
          A script already run on this machine replays from a local cache — same answers in
          seconds, because the model is never called. A script the machine has not seen is a
          real run: about 3 minutes and $0.80 for a half-hour episode.
        </p>

        {job && (
          <div className="space-y-2">
            <Progress value={job.percent} />
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">{job.message}</span>
              <span className="text-muted-foreground">{job.percent}%</span>
            </div>
            {job.log.length > 0 && (
              <div className="max-h-36 overflow-y-auto rounded-md bg-muted/50 p-2 font-mono text-[11px] leading-5">
                {job.log.map((line, i) => (
                  <div key={i} className="whitespace-pre">
                    {line}
                  </div>
                ))}
                <div ref={logEnd} />
              </div>
            )}
          </div>
        )}

        {job?.layout_warnings?.length ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            <Badge variant="outline" className="mr-2 border-amber-400">
              layout
            </Badge>
            This script's layout is degraded, so the Placement/Narrative labels on these
            rows are not trustworthy: {job.layout_warnings.join("; ")}
          </div>
        ) : null}

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
            {error}
            {error.includes("Failed to fetch") && (
              <div className="mt-1 text-muted-foreground">
                The backend is not answering. Start it with{" "}
                <code>uvicorn app.main:app --reload --port 8000</code> and{" "}
                <code>PLACEMENT_DEMO_ENABLED=true</code>.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
